import {
  HrefLink,
  IconPortal,
  IconProfile,
  IconSignOut,
  PrimaryButton
} from '@smartbear/swagger-portal-component-library'
import { clsx } from 'clsx'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { z } from 'zod'

import Form from '~/shared/components/Form'
import { ProfileDarkMode } from '~/shared/components/Profile/ProfileDarkMode'
import { ProfileSettings } from '~/shared/components/Profile/ProfileSettings'
import { useEnvironment } from '~/shared/hooks/useEnvironment.ts'
import { useShubOrgInfo } from '~/shared/hooks/useShubOrgInfo'
import { Environment } from '~/shared/types/environment'
import { UserInfo } from '~/shared/types/user.ts'
import { isAtLeastDesigner } from '~/shared/utils/roles'

export const Profile = ({
  user,
  isConsumerSide,
  showMode = true,
  close = () => { },
  platformAdmin,
  platformManaged,
}: {
  user: UserInfo,
  isConsumerSide: boolean,
  showMode?: boolean,
  close?: () => void,
  platformAdmin?: boolean,
  platformManaged?: boolean,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { highestRole, swaggerhubOrgId } = useShubOrgInfo()

  const env = useEnvironment()

  const showManageAccountLink = !user?.isExternal && (env !== Environment.ON_PREM || (env === Environment.ON_PREM && user?.authType?.toLowerCase() === 'internal'))

  return (
    <div className="sp-profile-menu w-[21.25rem] rounded bg-(--sp-color-surface) dark:bg-(--sp-color-surface-raised) shadow-[0px_3px_6px_0px_rgba(0,0,0,0.18)] dark:border dark:border-(--sp-color-border) dark:shadow-[0px_2px_4px_0px_rgba(23,54,71,0.12)]">

      <div className="flex items-center w-full h-20 leading-5">
        <div className="pl-4 flex-0 text-(--sp-color-text-muted)">
          <IconProfile />
        </div>
        <div className="flex-auto px-4 overflow-hidden">
          <div aria-label={t('components.profile.buttons.email.ariaLabel')} className="sp-profile-email mb-1 overflow-hidden font-semibold cursor-default text-ellipsis whitespace-nowrap text-body2 text-(--sp-color-text-muted)">
            {user.email}
          </div>
          {showManageAccountLink && <HrefLink
            aria-label={t('components.profile.buttons.manageAccount.ariaLabel')}
            label={t('components.profile.buttons.manageAccount.label')}
            href={user.profile}
            target="_blank"
            isExternal={true}
          />}
        </div>
      </div>
      {(isAtLeastDesigner(highestRole) || platformAdmin) && isConsumerSide ? (
        <div className="relative flex justify-center w-full px-4 py-5">
          <div className="absolute top-0 border-b inset-x-4 border-b-(--sp-color-border-subtle)" />
          <PrimaryButton
            aria-label={t('components.profile.buttons.provider.ariaLabel')}
            size="s"
            className="sp-profile-provider-button w-full"
            icon={<IconPortal />}
            onClick={() => navigate('/sp-admin/home')}
          >
            {t('components.profile.buttons.provider.label')}
          </PrimaryButton>
        </div>
      ) : null}
      {!isConsumerSide && <ProfileSettings swaggerhubOrgId={swaggerhubOrgId || ''} platformManaged={platformManaged} />}
      {showMode && <ProfileDarkMode close={close} />}
      <Form schema={z.object({})} method="get" action="/services/api/logout">
        {() => (
          <div
            aria-label={t('components.profile.buttons.logout.ariaLabel')}
            className={clsx(
              'relative flex w-full cursor-pointer rounded-b',
              'text-(--sp-color-text-muted) hover:bg-(--sp-color-surface-hover) hover:text-(--sp-color-text-strong)',
            )
            }>
            <input type="submit" name="submit" className="absolute inset-0 w-full h-full bg-transparent cursor-pointer" value="" />
            <div className="py-2 pl-4 flex-0">
              <IconSignOut />
            </div>
            <div className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap py-1.5 pr-4 pl-2 text-body2">
              {t('components.profile.buttons.logout.label')}
            </div>
          </div>
        )}
      </Form>

    </div>
  )
}